<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useQuery, useQueryClient } from '@tanstack/vue-query';
import { ManageOrganisationService } from '@/services/manageOrganisation.services';
import { ManageOrganisationMutation } from '@/mutations/manageOrganisation.mutations';
import { OrgContactMutation } from '@/mutations/orgContact.mutations';
import { OrgAddressMutation } from '@/mutations/orgAddress.mutations';
import type {
  IUpdateOrganisationBody,
  ICreateOrgContactBody,
  ICreateOrgAddressBody,
  IUpdateOrgContactBody,
  IUpdateOrgAddressBody,
} from '@/types/manageOrgCommerce.types';

const route = useRoute();
const router = useRouter();
const { t } = useI18n({ useScope: 'global' });
const queryClient = useQueryClient();
const id = computed(() => route.params.id as string);

const {
  handleUpdateOrganisation,
  handleUpdateOrganisationLogo,
  updateOrganisation,
  updateOrganisationLogo,
} = ManageOrganisationMutation();
const {
  handleCreateOrgContact,
  handleUpdateOrgContact,
  handleDeleteOrgContact,
} = OrgContactMutation();
const {
  handleCreateOrgAddress,
  handleUpdateOrgAddress,
  handleDeleteOrgAddress,
} = OrgAddressMutation();

// ── Constantes ─────────────────────────────────────────────────────────────
const ORG_TYPES = computed(() => [
  { value: 'COMPANY', label: t('editClient.type.COMPANY') },
  { value: 'INDIVIDUAL', label: t('editClient.type.INDIVIDUAL') },
  { value: 'ASSOCIATION', label: t('editClient.type.ASSOCIATION') },
  { value: 'PUBLIC_ENTITY', label: t('editClient.type.PUBLIC_ENTITY') },
]);
const POSITIONS = computed(() => [
  {
    value: 'legal_representative',
    label: t('editClient.positions.legal_representative'),
  },
  { value: 'ceo', label: t('editClient.positions.ceo') },
  { value: 'cfo', label: t('editClient.positions.cfo') },
  { value: 'cto', label: t('editClient.positions.cto') },
  { value: 'manager', label: t('editClient.positions.manager') },
  { value: 'accountant', label: t('editClient.positions.accountant') },
  {
    value: 'technical_contact',
    label: t('editClient.positions.technical_contact'),
  },
  {
    value: 'billing_contact',
    label: t('editClient.positions.billing_contact'),
  },
  {
    value: 'support_contact',
    label: t('editClient.positions.support_contact'),
  },
]);
const ADDR_LABELS = computed(() => [
  { value: 'headquarters', label: t('editClient.addrLabels.headquarters') },
  { value: 'billing', label: t('editClient.addrLabels.billing') },
  { value: 'warehouse', label: t('editClient.addrLabels.warehouse') },
  { value: 'shipping', label: t('editClient.addrLabels.shipping') },
  { value: 'branch', label: t('editClient.addrLabels.branch') },
]);
const POSITION_LABELS = computed<Record<string, string>>(() => ({
  legal_representative: t('editClient.positions.legal_representative'),
  ceo: t('editClient.positions.ceo'),
  cfo: t('editClient.positions.cfo'),
  cto: t('editClient.positions.cto'),
  manager: t('editClient.positions.manager'),
  accountant: t('editClient.positions.accountant'),
  technical_contact: t('editClient.positions.technical_contact'),
  billing_contact: t('editClient.positions.billing_contact'),
  support_contact: t('editClient.positions.support_contact'),
}));
const ADDR_LABEL_LABELS = computed<Record<string, string>>(() => ({
  headquarters: t('editClient.addrLabels.headquarters'),
  billing: t('editClient.addrLabels.billing'),
  warehouse: t('editClient.addrLabels.warehouse'),
  shipping: t('editClient.addrLabels.shipping'),
  branch: t('editClient.addrLabels.branch'),
}));

// ── ISO pays ───────────────────────────────────────────────────────────────
const ISO_FR: Record<string, string> = {
  AD: 'Andorre',
  AE: 'Émirats arabes unis',
  AF: 'Afghanistan',
  AG: 'Antigua-et-Barbuda',
  AI: 'Anguilla',
  AL: 'Albanie',
  AM: 'Arménie',
  AO: 'Angola',
  AR: 'Argentine',
  AS: 'Samoa américaines',
  AT: 'Autriche',
  AU: 'Australie',
  AW: 'Aruba',
  AX: 'Îles Åland',
  AZ: 'Azerbaïdjan',
  BA: 'Bosnie-Herzégovine',
  BB: 'Barbade',
  BD: 'Bangladesh',
  BE: 'Belgique',
  BF: 'Burkina Faso',
  BG: 'Bulgarie',
  BH: 'Bahreïn',
  BI: 'Burundi',
  BJ: 'Bénin',
  BL: 'Saint-Barthélemy',
  BM: 'Bermudes',
  BN: 'Brunéi',
  BO: 'Bolivie',
  BQ: 'Pays-Bas caribéens',
  BR: 'Brésil',
  BS: 'Bahamas',
  BT: 'Bhoutan',
  BW: 'Botswana',
  BY: 'Biélorussie',
  BZ: 'Belize',
  CA: 'Canada',
  CC: 'Îles Cocos',
  CD: 'Congo (RDC)',
  CF: 'Centrafrique',
  CG: 'Congo',
  CH: 'Suisse',
  CI: "Côte d'Ivoire",
  CK: 'Îles Cook',
  CL: 'Chili',
  CM: 'Cameroun',
  CN: 'Chine',
  CO: 'Colombie',
  CR: 'Costa Rica',
  CU: 'Cuba',
  CV: 'Cap-Vert',
  CW: 'Curaçao',
  CX: 'Île Christmas',
  CY: 'Chypre',
  CZ: 'Tchéquie',
  DE: 'Allemagne',
  DJ: 'Djibouti',
  DK: 'Danemark',
  DM: 'Dominique',
  DO: 'République dominicaine',
  DZ: 'Algérie',
  EC: 'Équateur',
  EE: 'Estonie',
  EG: 'Égypte',
  EH: 'Sahara occidental',
  ER: 'Érythrée',
  ES: 'Espagne',
  ET: 'Éthiopie',
  FI: 'Finlande',
  FJ: 'Fidji',
  FK: 'Îles Malouines',
  FM: 'Micronésie',
  FO: 'Îles Féroé',
  FR: 'France',
  GA: 'Gabon',
  GB: 'Royaume-Uni',
  GD: 'Grenade',
  GE: 'Géorgie',
  GF: 'Guyane française',
  GG: 'Guernesey',
  GH: 'Ghana',
  GI: 'Gibraltar',
  GL: 'Groenland',
  GM: 'Gambie',
  GN: 'Guinée',
  GP: 'Guadeloupe',
  GQ: 'Guinée équatoriale',
  GR: 'Grèce',
  GT: 'Guatemala',
  GU: 'Guam',
  GW: 'Guinée-Bissau',
  GY: 'Guyana',
  HK: 'Hong Kong',
  HN: 'Honduras',
  HR: 'Croatie',
  HT: 'Haïti',
  HU: 'Hongrie',
  ID: 'Indonésie',
  IE: 'Irlande',
  IL: 'Israël',
  IM: 'Île de Man',
  IN: 'Inde',
  IO: "Territoire britannique de l'océan Indien",
  IQ: 'Irak',
  IR: 'Iran',
  IS: 'Islande',
  IT: 'Italie',
  JE: 'Jersey',
  JM: 'Jamaïque',
  JO: 'Jordanie',
  JP: 'Japon',
  KE: 'Kenya',
  KG: 'Kirghizistan',
  KH: 'Cambodge',
  KI: 'Kiribati',
  KM: 'Comores',
  KN: 'Saint-Kitts-et-Nevis',
  KP: 'Corée du Nord',
  KR: 'Corée du Sud',
  KW: 'Koweït',
  KY: 'Îles Caïmans',
  KZ: 'Kazakhstan',
  LA: 'Laos',
  LB: 'Liban',
  LC: 'Sainte-Lucie',
  LI: 'Liechtenstein',
  LK: 'Sri Lanka',
  LR: 'Liberia',
  LS: 'Lesotho',
  LT: 'Lituanie',
  LU: 'Luxembourg',
  LV: 'Lettonie',
  LY: 'Libye',
  MA: 'Maroc',
  MC: 'Monaco',
  MD: 'Moldavie',
  ME: 'Monténégro',
  MF: 'Saint-Martin (partie française)',
  MG: 'Madagascar',
  MH: 'Îles Marshall',
  MK: 'Macédoine du Nord',
  ML: 'Mali',
  MM: 'Myanmar',
  MN: 'Mongolie',
  MO: 'Macao',
  MP: 'Îles Mariannes du Nord',
  MQ: 'Martinique',
  MR: 'Mauritanie',
  MS: 'Montserrat',
  MT: 'Malte',
  MU: 'Maurice',
  MV: 'Maldives',
  MW: 'Malawi',
  MX: 'Mexique',
  MY: 'Malaisie',
  MZ: 'Mozambique',
  NA: 'Namibie',
  NC: 'Nouvelle-Calédonie',
  NE: 'Niger',
  NF: 'Île Norfolk',
  NG: 'Nigéria',
  NI: 'Nicaragua',
  NL: 'Pays-Bas',
  NO: 'Norvège',
  NP: 'Népal',
  NR: 'Nauru',
  NU: 'Niue',
  NZ: 'Nouvelle-Zélande',
  OM: 'Oman',
  PA: 'Panama',
  PE: 'Pérou',
  PF: 'Polynésie française',
  PG: 'Papouasie-Nouvelle-Guinée',
  PH: 'Philippines',
  PK: 'Pakistan',
  PL: 'Pologne',
  PM: 'Saint-Pierre-et-Miquelon',
  PR: 'Porto Rico',
  PS: 'Palestine',
  PT: 'Portugal',
  PW: 'Palaos',
  PY: 'Paraguay',
  QA: 'Qatar',
  RE: 'La Réunion',
  RO: 'Roumanie',
  RS: 'Serbie',
  RU: 'Russie',
  RW: 'Rwanda',
  SA: 'Arabie saoudite',
  SB: 'Îles Salomon',
  SC: 'Seychelles',
  SD: 'Soudan',
  SE: 'Suède',
  SG: 'Singapour',
  SH: 'Sainte-Hélène',
  SI: 'Slovénie',
  SJ: 'Svalbard et Jan Mayen',
  SK: 'Slovaquie',
  SL: 'Sierra Leone',
  SM: 'Saint-Marin',
  SN: 'Sénégal',
  SO: 'Somalie',
  SR: 'Suriname',
  SS: 'Soudan du Sud',
  ST: 'Sao Tomé-et-Principe',
  SV: 'Salvador',
  SX: 'Saint-Martin (partie néerlandaise)',
  SY: 'Syrie',
  SZ: 'Eswatini',
  TC: 'Îles Turques-et-Caïques',
  TD: 'Tchad',
  TG: 'Togo',
  TH: 'Thaïlande',
  TJ: 'Tadjikistan',
  TK: 'Tokelau',
  TL: 'Timor oriental',
  TM: 'Turkménistan',
  TN: 'Tunisie',
  TO: 'Tonga',
  TR: 'Turquie',
  TT: 'Trinité-et-Tobago',
  TV: 'Tuvalu',
  TW: 'Taïwan',
  TZ: 'Tanzanie',
  UA: 'Ukraine',
  UG: 'Ouganda',
  US: 'États-Unis',
  UY: 'Uruguay',
  UZ: 'Ouzbékistan',
  VA: 'Vatican',
  VC: 'Saint-Vincent-et-les-Grenadines',
  VE: 'Venezuela',
  VG: 'Îles Vierges britanniques',
  VI: 'Îles Vierges américaines',
  VN: 'Viêt Nam',
  VU: 'Vanuatu',
  WF: 'Wallis-et-Futuna',
  WS: 'Samoa',
  XK: 'Kosovo',
  YE: 'Yémen',
  YT: 'Mayotte',
  ZA: 'Afrique du Sud',
  ZM: 'Zambie',
  ZW: 'Zimbabwe',
};
const countries = computed(() =>
  Object.entries(ISO_FR)
    .map(([code, nameFr]) => ({ code, nameFr }))
    .sort((a, b) => a.nameFr.localeCompare(b.nameFr, 'fr')),
);
function filteredCountries(search: string) {
  const q = search.toLowerCase();
  return q
    ? countries.value.filter((c) => c.nameFr.toLowerCase().includes(q))
    : countries.value;
}
function countryLabel(code: string) {
  return ISO_FR[code] ?? code;
}

// ── Chargement ─────────────────────────────────────────────────────────────
const {
  data: org,
  isPending,
  isError,
} = useQuery({
  queryKey: ['org-detail-full', id],
  queryFn: async () => {
    const r = await ManageOrganisationService.getOrganisationContactAdress(
      id.value,
    );
    return r.data as any;
  },
  enabled: computed(() => !!id.value),
});

const contacts = computed<any[]>(() => org.value?.contacts ?? []);
const addresses = computed<any[]>(() => org.value?.addresses ?? []);

function invalidate() {
  queryClient.invalidateQueries({ queryKey: ['org-detail-full', id] });
  queryClient.invalidateQueries({ queryKey: ['clients-page-orgs'] });
  queryClient.invalidateQueries({ queryKey: ['GetOrganisations'] });
}

// ── Logo ───────────────────────────────────────────────────────────────────
const logoFile = ref<File | null>(null);
const logoPreview = ref<string | null>(null);
const logoDropActive = ref(false);
const logoInputRef = ref<HTMLInputElement | null>(null);
const logoError = ref<string | null>(null);
const currentLogoUrl = computed(
  () => logoPreview.value ?? org.value?.logoUrl ?? null,
);

function onLogoChange(e: Event) {
  const f = (e.target as HTMLInputElement).files?.[0];
  if (f) setLogoFile(f);
}
function onLogoDrop(e: DragEvent) {
  logoDropActive.value = false;
  const f = e.dataTransfer?.files?.[0];
  if (f) setLogoFile(f);
}
function setLogoFile(file: File) {
  if (
    ![
      'image/png',
      'image/jpeg',
      'image/jpg',
      'image/webp',
      'image/svg+xml',
    ].includes(file.type)
  ) {
    logoError.value = t('editClient.logo.errorFormat');
    return;
  }
  if (file.size > 2 * 1024 * 1024) {
    logoError.value = t('editClient.logo.errorSize');
    return;
  }
  logoError.value = null;
  logoFile.value = file;
  logoPreview.value = URL.createObjectURL(file);
}
function removeLogo() {
  logoFile.value = null;
  if (logoPreview.value) {
    URL.revokeObjectURL(logoPreview.value);
    logoPreview.value = null;
  }
  if (logoInputRef.value) logoInputRef.value.value = '';
}

// ── Formulaire org ─────────────────────────────────────────────────────────
const form = ref({
  name: '',
  type: 'COMPANY',
  taxId: '',
  description: '',
  country: '',
});
const countryOrgOpen = ref(false);
const countryOrgSearch = ref('');

watch(
  org,
  (val) => {
    if (!val) return;
    form.value = {
      name: val.name ?? '',
      type: val.type ?? 'COMPANY',
      taxId: val.taxIdentificationNumber ?? '',
      description: val.description ?? '',
      country: val.countryCode ?? '',
    };
  },
  { immediate: true },
);

// ── Contacts — formulaire ajout/édition inline ─────────────────────────────
const showContactForm = ref(false);
const editContactId = ref<string | null>(null);
const contactSaving = ref(false);
const contactError = ref<string | null>(null);
const deletingContact = ref<string | null>(null);

// ── Modale de confirmation suppression ─────────────────────────────────────
const confirmModal = ref<{
  type: 'contact' | 'address';
  id: string;
  label: string;
} | null>(null);
const confirmDeleting = ref(false);

function askDeleteContact(c: any) {
  confirmModal.value = {
    type: 'contact',
    id: c.id,
    label: `${c.firstName} ${c.lastName}`,
  };
}
function askDeleteAddr(a: any) {
  const line =
    [a.addressLine1, a.city].filter(Boolean).join(', ') || 'cette adresse';
  confirmModal.value = { type: 'address', id: a.id, label: line };
}
function cancelConfirm() {
  if (!confirmDeleting.value) confirmModal.value = null;
}

async function confirmDelete() {
  if (!confirmModal.value || confirmDeleting.value) return;
  confirmDeleting.value = true;
  try {
    if (confirmModal.value.type === 'contact') {
      deletingContact.value = confirmModal.value.id;
      await handleDeleteOrgContact(id.value, confirmModal.value.id);
    } else {
      deletingAddr.value = confirmModal.value.id;
      await handleDeleteOrgAddress(id.value, confirmModal.value.id);
    }
    invalidate();
  } catch {
    /* silencieux */
  } finally {
    confirmDeleting.value = false;
    deletingContact.value = null;
    deletingAddr.value = null;
    confirmModal.value = null;
  }
}
const contactForm = ref({
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  position: 'legal_representative',
  isPrimary: false,
});

function openAddContact() {
  editContactId.value = null;
  contactForm.value = {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    position: 'legal_representative',
    isPrimary: false,
  };
  contactError.value = null;
  showContactForm.value = true;
}
function openEditContact(c: any) {
  editContactId.value = c.id;
  contactForm.value = {
    firstName: c.firstName ?? '',
    lastName: c.lastName ?? '',
    email: c.email ?? '',
    phone: c.phone ?? '',
    position: c.position ?? 'legal_representative',
    isPrimary: !!c.isPrimary,
  };
  contactError.value = null;
  showContactForm.value = true;
}
function cancelContact() {
  showContactForm.value = false;
  editContactId.value = null;
}

async function saveContact() {
  if (
    !contactForm.value.firstName.trim() ||
    !contactForm.value.lastName.trim()
  ) {
    contactError.value = t('editClient.contacts.errorRequired');
    return;
  }
  contactSaving.value = true;
  contactError.value = null;
  try {
    if (editContactId.value) {
      const body: IUpdateOrgContactBody = {
        firstName: contactForm.value.firstName,
        lastName: contactForm.value.lastName,
        email: contactForm.value.email || undefined,
        phone: contactForm.value.phone || undefined,
        position: contactForm.value.position as any,
        isPrimary: contactForm.value.isPrimary,
      };
      await handleUpdateOrgContact(id.value, editContactId.value, body);
    } else {
      const body: ICreateOrgContactBody = {
        firstName: contactForm.value.firstName,
        lastName: contactForm.value.lastName,
        email: contactForm.value.email || undefined,
        phone: contactForm.value.phone || undefined,
        position: contactForm.value.position as any,
        isPrimary: contactForm.value.isPrimary,
      };
      await handleCreateOrgContact(id.value, body);
    }
    invalidate();
    showContactForm.value = false;
    editContactId.value = null;
  } catch (err: any) {
    const raw = err?.response?.data?.message;
    contactError.value = Array.isArray(raw)
      ? raw[0]
      : (raw ?? err?.message ?? 'Erreur');
  } finally {
    contactSaving.value = false;
  }
}

// ── Adresses — formulaire ajout/édition inline ─────────────────────────────
const showAddrForm = ref(false);
const editAddrId = ref<string | null>(null);
const addrSaving = ref(false);
const addrError = ref<string | null>(null);
const deletingAddr = ref<string | null>(null);
const addrCountryOpen = ref(false);
const addrCountrySearch = ref('');
const addrForm = ref({
  addressLine1: '',
  addressLine2: '',
  city: '',
  postalCode: '',
  country: '',
  label: 'headquarters',
  isPrimary: false,
});

function openAddAddr() {
  editAddrId.value = null;
  addrForm.value = {
    addressLine1: '',
    addressLine2: '',
    city: '',
    postalCode: '',
    country: '',
    label: 'headquarters',
    isPrimary: false,
  };
  addrError.value = null;
  addrCountryOpen.value = false;
  addrCountrySearch.value = '';
  showAddrForm.value = true;
}
function openEditAddr(a: any) {
  editAddrId.value = a.id;
  addrForm.value = {
    addressLine1: a.addressLine1 ?? '',
    addressLine2: a.addressLine2 ?? '',
    city: a.city ?? '',
    postalCode: a.postalCode ?? '',
    country: a.country ?? '',
    label: a.label ?? 'headquarters',
    isPrimary: !!a.isPrimary,
  };
  addrError.value = null;
  addrCountryOpen.value = false;
  addrCountrySearch.value = '';
  showAddrForm.value = true;
}
function cancelAddr() {
  showAddrForm.value = false;
  editAddrId.value = null;
}

async function saveAddr() {
  if (
    !addrForm.value.addressLine1.trim() ||
    !addrForm.value.city.trim() ||
    !addrForm.value.postalCode.trim() ||
    !addrForm.value.country
  ) {
    addrError.value = t('editClient.addresses.errorRequired');
    return;
  }
  addrSaving.value = true;
  addrError.value = null;
  try {
    if (editAddrId.value) {
      const body: IUpdateOrgAddressBody = {
        addressLine1: addrForm.value.addressLine1,
        addressLine2: addrForm.value.addressLine2 || undefined,
        city: addrForm.value.city,
        postalCode: addrForm.value.postalCode,
        country: addrForm.value.country,
        label: addrForm.value.label as any,
        isPrimary: addrForm.value.isPrimary,
      };
      await handleUpdateOrgAddress(id.value, editAddrId.value, body);
    } else {
      const body: ICreateOrgAddressBody = {
        addressLine1: addrForm.value.addressLine1,
        addressLine2: addrForm.value.addressLine2 || undefined,
        city: addrForm.value.city,
        postalCode: addrForm.value.postalCode,
        country: addrForm.value.country,
        label: addrForm.value.label as any,
        isPrimary: addrForm.value.isPrimary,
      };
      await handleCreateOrgAddress(id.value, body);
    }
    invalidate();
    showAddrForm.value = false;
    editAddrId.value = null;
  } catch (err: any) {
    const raw = err?.response?.data?.message;
    addrError.value = Array.isArray(raw)
      ? raw[0]
      : (raw ?? err?.message ?? 'Erreur');
  } finally {
    addrSaving.value = false;
  }
}

// ── Soumission org ─────────────────────────────────────────────────────────
const submitError = ref<string | null>(null);
const submitSuccess = ref(false);
const isSaving = computed(
  () =>
    updateOrganisation.isPending.value ||
    updateOrganisationLogo.isPending.value,
);

async function submit() {
  if (!form.value.name.trim() || isSaving.value) return;
  submitError.value = null;
  try {
    const body: IUpdateOrganisationBody = {
      name: form.value.name || undefined,
      type: (form.value.type as any) || undefined,
      taxIdentificationNumber: form.value.taxId || undefined,
      description: form.value.description || undefined,
      country: form.value.country || undefined,
    };
    await handleUpdateOrganisation(id.value, body);
    if (logoFile.value)
      await handleUpdateOrganisationLogo(id.value, logoFile.value);
    invalidate();
    submitSuccess.value = true;
  } catch (err: any) {
    const raw = err?.response?.data?.message;
    submitError.value = Array.isArray(raw)
      ? raw[0]
      : (raw ?? err?.message ?? 'Une erreur est survenue');
  }
}
</script>

<template>
  <div
    class="ecp"
    @click="
      countryOrgOpen = false;
      addrCountryOpen = false;
    "
  >
    <!-- Breadcrumb -->
    <nav class="ecp__breadcrumb">
      <button class="ecp__bc-link" @click="router.push('/clients')">
        {{ t('editClient.breadcrumb') }}
      </button>
      <i class="pi pi-angle-right ecp__bc-sep" />
      <button class="ecp__bc-link" @click="router.push(`/clients/${id}`)">
        {{ org?.name ?? '…' }}
      </button>
      <i class="pi pi-angle-right ecp__bc-sep" />
      <span class="ecp__bc-cur">{{ t('editClient.bcEdit') }}</span>
    </nav>

    <!-- Skeleton -->
    <div v-if="isPending" class="ecp__skeleton">
      <div class="ecp__sk ecp__sk--header" />
      <div class="ecp__sk ecp__sk--body" />
      <div class="ecp__sk ecp__sk--body" />
    </div>

    <!-- Erreur -->
    <div v-else-if="isError" class="ecp__load-error">
      <i class="pi pi-exclamation-circle" />
      <p>{{ t('editClient.loadError') }}</p>
      <button class="ecp__btn ecp__btn--ghost" @click="router.back()">
        <i class="pi pi-arrow-left" /> {{ t('editClient.back') }}
      </button>
    </div>

    <!-- Succès -->
    <div v-else-if="submitSuccess" class="ecp__success">
      <div class="ecp__success-icon"><i class="pi pi-check" /></div>
      <h2 class="ecp__success-title">{{ t('editClient.success.title') }}</h2>
      <p class="ecp__success-sub">{{ t('editClient.success.sub') }}</p>
      <div class="ecp__success-actions">
        <button
          class="ecp__btn ecp__btn--ghost"
          @click="router.push('/clients')"
        >
          <i class="pi pi-arrow-left" /> {{ t('editClient.success.backList') }}
        </button>
        <button
          class="ecp__btn ecp__btn--primary"
          @click="router.push(`/clients/${id}`)"
        >
          <i class="pi pi-eye" /> {{ t('editClient.success.viewClient') }}
        </button>
      </div>
    </div>

    <template v-else-if="org">
      <div class="ecp__header">
        <button class="ecp__back" @click="router.push(`/clients/${id}`)">
          <i class="pi pi-arrow-left" />
        </button>
        <div>
          <h1 class="ecp__title">{{ t('editClient.title') }}</h1>
          <p class="ecp__sub">{{ org.name }}</p>
        </div>
      </div>

      <!-- ═══ CARD 1 : Logo + Organisation ═══ -->
      <div class="ecp__body">
        <!-- Logo -->
        <section class="ecp__section">
          <div class="ecp__section-header">
            <div class="ecp__section-icon"><i class="pi pi-image" /></div>
            <div>
              <p class="ecp__section-title">
                {{ t('editClient.logo.sectionTitle') }}
              </p>
              <p class="ecp__section-sub">
                {{ t('editClient.logo.sectionSub') }}
              </p>
            </div>
          </div>
          <div class="ecp__logo-area">
            <div
              class="ecp__logo-preview"
              :class="{ 'ecp__logo-preview--empty': !currentLogoUrl }"
            >
              <img
                v-if="currentLogoUrl"
                :src="currentLogoUrl"
                alt="Logo"
                class="ecp__logo-img"
              />
              <i v-else class="pi pi-building ecp__logo-ph" />
            </div>
            <div
              class="ecp__logo-drop"
              :class="{ 'ecp__logo-drop--active': logoDropActive }"
              @dragover.prevent="logoDropActive = true"
              @dragleave="logoDropActive = false"
              @drop.prevent="onLogoDrop"
              @click="logoInputRef?.click()"
            >
              <i class="pi pi-upload ecp__logo-drop-icon" />
              <span class="ecp__logo-drop-label"
                >{{ t('editClient.logo.dropLabel') }}
                <span class="ecp__logo-drop-link">{{
                  t('editClient.logo.dropLink')
                }}</span></span
              >
              <span class="ecp__logo-drop-hint">{{
                t('editClient.logo.dropHint')
              }}</span>
              <input
                ref="logoInputRef"
                type="file"
                accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml"
                class="ecp__logo-input"
                @change="onLogoChange"
              />
            </div>
          </div>
          <div v-if="logoFile" class="ecp__logo-selected">
            <i class="pi pi-check-circle ecp__logo-selected-icon" />
            <span class="ecp__logo-selected-name">{{ logoFile.name }}</span>
            <button class="ecp__logo-remove" @click="removeLogo">
              <i class="pi pi-times" />
            </button>
          </div>
          <p v-if="logoError" class="ecp__field-error">
            <i class="pi pi-exclamation-circle" /> {{ logoError }}
          </p>
        </section>

        <div class="ecp__divider" />

        <!-- Organisation -->
        <section class="ecp__section">
          <div class="ecp__section-header">
            <div class="ecp__section-icon"><i class="pi pi-building" /></div>
            <div>
              <p class="ecp__section-title">
                {{ t('editClient.org.sectionTitle') }}
              </p>
              <p class="ecp__section-sub">
                {{ t('editClient.org.sectionSub') }}
              </p>
            </div>
          </div>
          <div class="ecp__fields">
            <div class="ecp__field-row">
              <div class="ecp__field ecp__field--span2">
                <label class="ecp__label"
                  >{{ t('editClient.org.name') }}
                  <span class="ecp__req">*</span></label
                >
                <input
                  v-model="form.name"
                  class="ecp__input"
                  type="text"
                  placeholder="Acme Belgium SA"
                />
              </div>
            </div>
            <div class="ecp__field-row">
              <div class="ecp__field">
                <label class="ecp__label">{{ t('editClient.org.type') }}</label>
                <div class="ecp__select-wrap">
                  <select v-model="form.type" class="ecp__select">
                    <option
                      v-for="opt in ORG_TYPES"
                      :key="opt.value"
                      :value="opt.value"
                    >
                      {{ opt.label }}
                    </option>
                  </select>
                  <i class="pi pi-chevron-down ecp__select-icon" />
                </div>
              </div>
              <div class="ecp__field">
                <label class="ecp__label">{{ t('editClient.org.tax') }}</label>
                <input
                  v-model="form.taxId"
                  class="ecp__input"
                  type="text"
                  placeholder="BE0123456789"
                />
              </div>
            </div>
            <div class="ecp__field-row">
              <div class="ecp__field">
                <label class="ecp__label">{{
                  t('editClient.org.country')
                }}</label>
                <div class="ecp__dd" @click.stop>
                  <button
                    type="button"
                    class="ecp__dd-trigger"
                    :class="{ 'ecp__dd-trigger--open': countryOrgOpen }"
                    @click="countryOrgOpen = !countryOrgOpen"
                  >
                    <span :class="form.country ? '' : 'ecp__dd-placeholder'">{{
                      form.country
                        ? countryLabel(form.country)
                        : t('editClient.org.selectCountry')
                    }}</span>
                    <i
                      class="pi pi-chevron-down ecp__dd-arrow"
                      :class="{ 'ecp__dd-arrow--open': countryOrgOpen }"
                    />
                  </button>
                  <div v-if="countryOrgOpen" class="ecp__dd-panel">
                    <div class="ecp__dd-search-wrap">
                      <i class="pi pi-search ecp__dd-search-icon" /><input
                        v-model="countryOrgSearch"
                        class="ecp__dd-search"
                        type="text"
                        :placeholder="t('editClient.org.searchCountry')"
                        @click.stop
                        autofocus
                      />
                    </div>
                    <ul class="ecp__dd-list">
                      <li
                        v-for="c in filteredCountries(countryOrgSearch)"
                        :key="c.code"
                        class="ecp__dd-item"
                        :class="{
                          'ecp__dd-item--active': form.country === c.code,
                        }"
                        @click="
                          form.country = c.code;
                          countryOrgOpen = false;
                          countryOrgSearch = '';
                        "
                      >
                        {{ c.nameFr }}
                      </li>
                      <li
                        v-if="filteredCountries(countryOrgSearch).length === 0"
                        class="ecp__dd-empty"
                      >
                        {{ t('editClient.org.noResult') }}
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
              <div class="ecp__field">
                <label class="ecp__label">{{
                  t('editClient.org.description')
                }}</label>
                <input
                  v-model="form.description"
                  class="ecp__input"
                  type="text"
                  :placeholder="t('editClient.org.descPlaceholder')"
                />
              </div>
            </div>
          </div>
        </section>

        <div v-if="submitError" class="ecp__error-banner">
          <i class="pi pi-exclamation-circle" /> {{ submitError }}
        </div>

        <div class="ecp__actions">
          <button
            class="ecp__btn ecp__btn--ghost"
            @click="router.push(`/clients/${id}`)"
          >
            <i class="pi pi-times" /> {{ t('editClient.cancel') }}
          </button>
          <button
            class="ecp__btn ecp__btn--primary"
            :disabled="!form.name.trim() || isSaving"
            @click="submit"
          >
            <span v-if="isSaving" class="ecp__spinner ecp__spinner--white" />
            <i v-else class="pi pi-check" />
            {{ isSaving ? t('editClient.saving') : t('editClient.save') }}
          </button>
        </div>
      </div>

      <!-- ═══ CARD 2 : Contacts ═══ -->
      <div class="ecp__body">
        <section class="ecp__section">
          <div class="ecp__section-header">
            <div class="ecp__section-icon"><i class="pi pi-users" /></div>
            <div>
              <p class="ecp__section-title">
                {{ t('editClient.contacts.sectionTitle') }}
              </p>
              <p class="ecp__section-sub">
                {{ t('editClient.contacts.sectionSub') }}
              </p>
            </div>
            <button
              class="ecp__add-btn"
              @click="openAddContact"
              :disabled="showContactForm"
            >
              <i class="pi pi-plus" /> {{ t('editClient.contacts.add') }}
            </button>
          </div>

          <!-- Liste contacts -->
          <div class="ecp__items" v-if="contacts.length > 0 || showContactForm">
            <div v-for="c in contacts" :key="c.id" class="ecp__item">
              <div class="ecp__item-avatar">
                <span>{{ (c.firstName?.[0] ?? '?').toUpperCase() }}</span>
              </div>
              <div class="ecp__item-info">
                <div class="ecp__item-name">
                  {{ c.firstName }} {{ c.lastName }}
                  <span v-if="c.isPrimary" class="ecp__item-primary">
                    {{ t('editClient.contacts.primary') }}
                  </span>
                </div>
                <div class="ecp__item-meta">
                  <span v-if="c.position">{{
                    POSITION_LABELS[c.position] ?? c.position
                  }}</span>
                  <span v-if="c.email" class="ecp__item-sep">·</span>
                  <span v-if="c.email">{{ c.email }}</span>
                  <span v-if="c.phone" class="ecp__item-sep">·</span>
                  <span v-if="c.phone">{{ c.phone }}</span>
                </div>
              </div>
              <div class="ecp__item-actions">
                <button
                  class="ecp__icon-btn"
                  :title="t('editClient.contacts.editBtn')"
                  @click="openEditContact(c)"
                  :disabled="showContactForm"
                >
                  <i class="pi pi-pencil" />
                </button>
                <button
                  class="ecp__icon-btn ecp__icon-btn--danger"
                  :title="t('editClient.contacts.deleteBtn')"
                  @click="askDeleteContact(c)"
                  :disabled="!!deletingContact"
                >
                  <span v-if="deletingContact === c.id" class="ecp__spinner" />
                  <i v-else class="pi pi-trash" />
                </button>
              </div>
            </div>

            <!-- Formulaire inline contact -->
            <div v-if="showContactForm" class="ecp__inline-form">
              <p class="ecp__inline-form-title">
                {{
                  editContactId
                    ? t('editClient.contacts.formEdit')
                    : t('editClient.contacts.formAdd')
                }}
              </p>
              <div class="ecp__fields">
                <div class="ecp__field-row">
                  <div class="ecp__field">
                    <label class="ecp__label"
                      >{{ t('editClient.contacts.lastName') }}
                      <span class="ecp__req">*</span></label
                    >
                    <input
                      v-model="contactForm.lastName"
                      class="ecp__input"
                      type="text"
                      placeholder="Dupont"
                    />
                  </div>
                  <div class="ecp__field">
                    <label class="ecp__label"
                      >{{ t('editClient.contacts.firstName') }}
                      <span class="ecp__req">*</span></label
                    >
                    <input
                      v-model="contactForm.firstName"
                      class="ecp__input"
                      type="text"
                      placeholder="Jean"
                    />
                  </div>
                </div>
                <div class="ecp__field-row">
                  <div class="ecp__field">
                    <label class="ecp__label">{{
                      t('editClient.contacts.email')
                    }}</label>
                    <input
                      v-model="contactForm.email"
                      class="ecp__input"
                      type="email"
                      placeholder="jean@exemple.com"
                    />
                  </div>
                  <div class="ecp__field">
                    <label class="ecp__label">{{
                      t('editClient.contacts.phone')
                    }}</label>
                    <input
                      v-model="contactForm.phone"
                      class="ecp__input"
                      type="tel"
                      placeholder="+32 470 00 00 00"
                    />
                  </div>
                </div>
                <div class="ecp__field-row">
                  <div class="ecp__field">
                    <label class="ecp__label">{{
                      t('editClient.contacts.position')
                    }}</label>
                    <div class="ecp__select-wrap">
                      <select
                        v-model="contactForm.position"
                        class="ecp__select"
                      >
                        <option
                          v-for="p in POSITIONS"
                          :key="p.value"
                          :value="p.value"
                        >
                          {{ p.label }}
                        </option>
                      </select>
                      <i class="pi pi-chevron-down ecp__select-icon" />
                    </div>
                  </div>
                  <div class="ecp__field ecp__field--center">
                    <label class="ecp__label">{{
                      t('editClient.contacts.isPrimary')
                    }}</label>
                    <label class="ecp__toggle">
                      <input
                        type="checkbox"
                        v-model="contactForm.isPrimary"
                        class="ecp__toggle-input"
                      />
                      <span class="ecp__toggle-track"
                        ><span class="ecp__toggle-thumb"
                      /></span>
                    </label>
                  </div>
                </div>
              </div>
              <div v-if="contactError" class="ecp__error-banner">
                <i class="pi pi-exclamation-circle" /> {{ contactError }}
              </div>
              <div class="ecp__inline-actions">
                <button class="ecp__btn ecp__btn--ghost" @click="cancelContact">
                  <i class="pi pi-times" />
                  {{ t('editClient.contacts.cancel') }}
                </button>
                <button
                  class="ecp__btn ecp__btn--primary"
                  @click="saveContact"
                  :disabled="contactSaving"
                >
                  <span
                    v-if="contactSaving"
                    class="ecp__spinner ecp__spinner--white"
                  />
                  <i v-else class="pi pi-check" />
                  {{
                    editContactId
                      ? t('editClient.contacts.saving')
                      : t('editClient.contacts.save')
                  }}
                </button>
              </div>
            </div>
          </div>

          <div v-else class="ecp__empty">
            <i class="pi pi-user ecp__empty-icon" />
            <p>{{ t('editClient.contacts.empty') }}</p>
          </div>
        </section>
      </div>

      <!-- ═══ CARD 3 : Adresses ═══ -->
      <div class="ecp__body">
        <section class="ecp__section">
          <div class="ecp__section-header">
            <div class="ecp__section-icon"><i class="pi pi-map-marker" /></div>
            <div>
              <p class="ecp__section-title">
                {{ t('editClient.addresses.sectionTitle') }}
              </p>
              <p class="ecp__section-sub">
                {{ t('editClient.addresses.sectionSub') }}
              </p>
            </div>
            <button
              class="ecp__add-btn"
              @click="openAddAddr"
              :disabled="showAddrForm"
            >
              <i class="pi pi-plus" /> {{ t('editClient.addresses.add') }}
            </button>
          </div>

          <!-- Liste adresses -->
          <div class="ecp__items" v-if="addresses.length > 0 || showAddrForm">
            <div v-for="a in addresses" :key="a.id" class="ecp__item">
              <div class="ecp__item-avatar ecp__item-avatar--addr">
                <i class="pi pi-map-marker" />
              </div>
              <div class="ecp__item-info">
                <div class="ecp__item-name">
                  {{ a.addressLine1
                  }}<template v-if="a.city">, {{ a.city }}</template>
                  <span v-if="a.isPrimary" class="ecp__item-primary">
                    {{ t('editClient.addresses.primary') }}
                  </span>
                </div>
                <div class="ecp__item-meta">
                  <span v-if="a.postalCode">{{ a.postalCode }}</span>
                  <span v-if="a.country" class="ecp__item-sep">·</span>
                  <span v-if="a.country">{{ countryLabel(a.country) }}</span>
                  <span v-if="a.label" class="ecp__item-sep">·</span>
                  <span v-if="a.label">{{
                    ADDR_LABEL_LABELS[a.label] ?? a.label
                  }}</span>
                </div>
              </div>
              <div class="ecp__item-actions">
                <button
                  class="ecp__icon-btn"
                  :title="t('editClient.addresses.editBtn')"
                  @click="openEditAddr(a)"
                  :disabled="showAddrForm"
                >
                  <i class="pi pi-pencil" />
                </button>
                <button
                  class="ecp__icon-btn ecp__icon-btn--danger"
                  :title="t('editClient.addresses.deleteBtn')"
                  @click="askDeleteAddr(a)"
                  :disabled="!!deletingAddr"
                >
                  <span v-if="deletingAddr === a.id" class="ecp__spinner" />
                  <i v-else class="pi pi-trash" />
                </button>
              </div>
            </div>

            <!-- Formulaire inline adresse -->
            <div v-if="showAddrForm" class="ecp__inline-form">
              <p class="ecp__inline-form-title">
                {{
                  editAddrId
                    ? t('editClient.addresses.formEdit')
                    : t('editClient.addresses.formAdd')
                }}
              </p>
              <div class="ecp__fields">
                <div class="ecp__field-row">
                  <div class="ecp__field">
                    <label class="ecp__label"
                      >{{ t('editClient.addresses.line1') }}
                      <span class="ecp__req">*</span></label
                    >
                    <input
                      v-model="addrForm.addressLine1"
                      class="ecp__input"
                      type="text"
                      placeholder="Rue de la Loi 1"
                    />
                  </div>
                  <div class="ecp__field">
                    <label class="ecp__label">{{
                      t('editClient.addresses.line2')
                    }}</label>
                    <input
                      v-model="addrForm.addressLine2"
                      class="ecp__input"
                      type="text"
                      :placeholder="t('editClient.addresses.line2Placeholder')"
                    />
                  </div>
                </div>
                <div class="ecp__field-row">
                  <div class="ecp__field">
                    <label class="ecp__label"
                      >{{ t('editClient.addresses.city') }}
                      <span class="ecp__req">*</span></label
                    >
                    <input
                      v-model="addrForm.city"
                      class="ecp__input"
                      type="text"
                      placeholder="Bruxelles"
                    />
                  </div>
                  <div class="ecp__field">
                    <label class="ecp__label"
                      >{{ t('editClient.addresses.postal') }}
                      <span class="ecp__req">*</span></label
                    >
                    <input
                      v-model="addrForm.postalCode"
                      class="ecp__input"
                      type="text"
                      placeholder="1000"
                    />
                  </div>
                </div>
                <div class="ecp__field-row">
                  <div class="ecp__field">
                    <label class="ecp__label"
                      >{{ t('editClient.addresses.country') }}
                      <span class="ecp__req">*</span></label
                    >
                    <div class="ecp__dd" @click.stop>
                      <button
                        type="button"
                        class="ecp__dd-trigger"
                        :class="{ 'ecp__dd-trigger--open': addrCountryOpen }"
                        @click="addrCountryOpen = !addrCountryOpen"
                      >
                        <span
                          :class="addrForm.country ? '' : 'ecp__dd-placeholder'"
                          >{{
                            addrForm.country
                              ? countryLabel(addrForm.country)
                              : t('editClient.addresses.selectCountry')
                          }}</span
                        >
                        <i
                          class="pi pi-chevron-down ecp__dd-arrow"
                          :class="{ 'ecp__dd-arrow--open': addrCountryOpen }"
                        />
                      </button>
                      <div v-if="addrCountryOpen" class="ecp__dd-panel">
                        <div class="ecp__dd-search-wrap">
                          <i class="pi pi-search ecp__dd-search-icon" /><input
                            v-model="addrCountrySearch"
                            class="ecp__dd-search"
                            type="text"
                            :placeholder="
                              t('editClient.addresses.searchCountry')
                            "
                            @click.stop
                            autofocus
                          />
                        </div>
                        <ul class="ecp__dd-list">
                          <li
                            v-for="c in filteredCountries(addrCountrySearch)"
                            :key="c.code"
                            class="ecp__dd-item"
                            :class="{
                              'ecp__dd-item--active':
                                addrForm.country === c.code,
                            }"
                            @click="
                              addrForm.country = c.code;
                              addrCountryOpen = false;
                              addrCountrySearch = '';
                            "
                          >
                            {{ c.nameFr }}
                          </li>
                          <li
                            v-if="
                              filteredCountries(addrCountrySearch).length === 0
                            "
                            class="ecp__dd-empty"
                          >
                            {{ t('editClient.addresses.noResult') }}
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                  <div class="ecp__field">
                    <label class="ecp__label">{{
                      t('editClient.addresses.addrType')
                    }}</label>
                    <div class="ecp__select-wrap">
                      <select v-model="addrForm.label" class="ecp__select">
                        <option
                          v-for="l in ADDR_LABELS"
                          :key="l.value"
                          :value="l.value"
                        >
                          {{ l.label }}
                        </option>
                      </select>
                      <i class="pi pi-chevron-down ecp__select-icon" />
                    </div>
                  </div>
                </div>
                <div class="ecp__field">
                  <label class="ecp__toggle-label">
                    <label class="ecp__toggle">
                      <input
                        type="checkbox"
                        v-model="addrForm.isPrimary"
                        class="ecp__toggle-input"
                      />
                      <span class="ecp__toggle-track"
                        ><span class="ecp__toggle-thumb"
                      /></span>
                    </label>
                    {{ t('editClient.addresses.isPrimary') }}
                  </label>
                </div>
              </div>
              <div v-if="addrError" class="ecp__error-banner">
                <i class="pi pi-exclamation-circle" /> {{ addrError }}
              </div>
              <div class="ecp__inline-actions">
                <button class="ecp__btn ecp__btn--ghost" @click="cancelAddr">
                  <i class="pi pi-times" />
                  {{ t('editClient.addresses.cancel') }}
                </button>
                <button
                  class="ecp__btn ecp__btn--primary"
                  @click="saveAddr"
                  :disabled="addrSaving"
                >
                  <span
                    v-if="addrSaving"
                    class="ecp__spinner ecp__spinner--white"
                  />
                  <i v-else class="pi pi-check" />
                  {{
                    editAddrId
                      ? t('editClient.addresses.saving')
                      : t('editClient.addresses.save')
                  }}
                </button>
              </div>
            </div>
          </div>

          <div v-else class="ecp__empty">
            <i class="pi pi-map-marker ecp__empty-icon" />
            <p>{{ t('editClient.addresses.empty') }}</p>
          </div>
        </section>
      </div>
    </template>

    <!-- ═══ Modale confirmation suppression ═══ -->
    <Teleport to="body">
      <Transition name="ecp-modal">
        <div
          v-if="confirmModal"
          class="ecp__modal-overlay"
          @click.self="cancelConfirm"
        >
          <div class="ecp__modal" role="dialog" aria-modal="true">
            <div class="ecp__modal-icon">
              <i class="pi pi-exclamation-triangle" />
            </div>
            <h3 class="ecp__modal-title">
              {{
                confirmModal.type === 'contact'
                  ? t('editClient.deleteModal.titleContact')
                  : t('editClient.deleteModal.titleAddress')
              }}
            </h3>
            <p class="ecp__modal-desc">
              <strong>{{ confirmModal.label }}</strong>
              {{
                confirmModal.type === 'contact'
                  ? t('editClient.deleteModal.body', { label: '' })
                  : t('editClient.deleteModal.bodyFem', { label: '' })
              }}
            </p>
            <div class="ecp__modal-actions">
              <button
                class="ecp__btn ecp__btn--ghost"
                @click="cancelConfirm"
                :disabled="confirmDeleting"
              >
                <i class="pi pi-times" />
                {{ t('editClient.deleteModal.cancel') }}
              </button>
              <button
                class="ecp__btn ecp__btn--danger"
                @click="confirmDelete"
                :disabled="confirmDeleting"
              >
                <span
                  v-if="confirmDeleting"
                  class="ecp__spinner ecp__spinner--white"
                />
                <i v-else class="pi pi-trash" />
                {{
                  confirmDeleting
                    ? t('editClient.deleteModal.confirming')
                    : t('editClient.deleteModal.confirm')
                }}
              </button>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<style scoped>
.ecp {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  min-width: 0;
  padding: 2.5rem 3rem;
  box-sizing: border-box;
}
@media (max-width: 1023px) {
  .ecp {
    padding: 1.5rem;
  }
}
@media (max-width: 639px) {
  .ecp {
    padding: 1rem;
    gap: 1rem;
  }
}

.ecp__breadcrumb {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-muted);
}
.ecp__bc-link {
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-size: 0.8125rem;
  font-family: inherit;
  color: var(--nxt-on-surface-muted);
  transition: color 0.15s;
}
.ecp__bc-link:hover {
  color: var(--nxt-primary);
}
.ecp__bc-sep {
  font-size: 0.625rem;
}
.ecp__bc-cur {
  font-weight: 700;
  color: var(--nxt-on-surface);
}

.ecp__skeleton {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
.ecp__sk {
  border-radius: 1rem;
  background: linear-gradient(
    90deg,
    var(--nxt-surface-low) 25%,
    var(--nxt-surface-mid) 50%,
    var(--nxt-surface-low) 75%
  );
  background-size: 200% 100%;
  animation: ecp-shimmer 1.4s infinite;
}
.ecp__sk--header {
  height: 5rem;
}
.ecp__sk--body {
  height: 22rem;
}
@keyframes ecp-shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

.ecp__load-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 3rem;
  text-align: center;
  background: var(--nxt-card-bg);
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 1.25rem;
}
.ecp__load-error i {
  font-size: 2rem;
  color: var(--nxt-error);
}
.ecp__load-error p {
  margin: 0;
  color: var(--nxt-on-surface-muted);
}

.ecp__header {
  display: flex;
  align-items: center;
  gap: 1rem;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 1.5rem;
  padding: 1.25rem 1.5rem;
  box-shadow: 0 4px 15px rgba(142, 142, 142, 0.06);
  transition:
    border-color 0.2s,
    box-shadow 0.2s;
}
.ecp__back {
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 50%;
  flex-shrink: 0;
  border: 1px solid var(--nxt-outline-variant);
  background: var(--nxt-surface-low);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  color: var(--nxt-on-surface-variant);
  cursor: pointer;
  transition: all 0.15s;
}
.ecp__back:hover {
  background: var(--nxt-surface-mid);
  border-color: var(--nxt-outline);
  color: var(--nxt-on-surface);
}
.ecp__title {
  margin: 0 0 0.15rem;
  font-size: 1.125rem;
  font-weight: 800;
  letter-spacing: -0.02em;
  color: var(--nxt-on-surface);
}
.ecp__sub {
  margin: 0;
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-muted);
}

.ecp__body {
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 1.5rem;
  box-shadow: 0 4px 15px rgba(142, 142, 142, 0.06);
  padding: 2rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.ecp__section {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}
.ecp__section-header {
  display: flex;
  align-items: flex-start;
  gap: 0.875rem;
}
.ecp__section-icon {
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 0.625rem;
  flex-shrink: 0;
  background: var(--nxt-surface-mid);
  border: 1px solid var(--nxt-outline-variant);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.9375rem;
  color: var(--nxt-on-surface-variant);
}
.ecp__section-title {
  margin: 0 0 0.1rem;
  font-size: 0.9375rem;
  font-weight: 700;
  color: var(--nxt-on-surface);
}
.ecp__section-sub {
  margin: 0;
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-muted);
}
.ecp__divider {
  height: 1px;
  background: var(--nxt-outline-variant);
}

.ecp__add-btn {
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  height: 2rem;
  padding: 0 0.875rem;
  border-radius: 0.5rem;
  border: 1px solid var(--nxt-primary);
  background: rgba(0, 0, 0, 0.06);
  color: var(--nxt-primary);
  font-size: 0.8125rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s;
  flex-shrink: 0;
}
.ecp__add-btn:hover:not(:disabled) {
  background: rgba(0, 0, 0, 0.12);
}
.ecp__add-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.ecp__fields {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
.ecp__field-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}
.ecp__field--span2 {
  grid-column: span 2;
}
.ecp__field--center {
  justify-content: flex-start;
}
@media (max-width: 620px) {
  .ecp__field-row {
    grid-template-columns: 1fr;
  }
  .ecp__field--span2 {
    grid-column: span 1;
  }
}
.ecp__field {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}
.ecp__label {
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--nxt-on-surface);
}
.ecp__req {
  color: var(--nxt-error);
}

.ecp__input {
  height: 2.625rem;
  padding: 0 0.875rem;
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.625rem;
  background: var(--nxt-surface-low);
  color: var(--nxt-on-surface);
  font-size: 0.875rem;
  font-family: inherit;
  outline: none;
  transition:
    border-color 0.15s,
    background 0.15s;
  width: 100%;
  box-sizing: border-box;
}
.ecp__input::placeholder {
  color: var(--nxt-on-surface-muted);
}
.ecp__input:focus {
  border-color: var(--nxt-primary);
  background: var(--nxt-card-bg);
}

.ecp__select-wrap {
  position: relative;
}
.ecp__select {
  width: 100%;
  height: 2.625rem;
  padding: 0 2.5rem 0 0.875rem;
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.625rem;
  background: var(--nxt-surface-low);
  color: var(--nxt-on-surface);
  font-size: 0.875rem;
  font-family: inherit;
  outline: none;
  appearance: none;
  -webkit-appearance: none;
  cursor: pointer;
  transition: border-color 0.15s;
}
.ecp__select:focus {
  border-color: var(--nxt-primary);
}
.ecp__select-icon {
  position: absolute;
  right: 0.875rem;
  top: 50%;
  transform: translateY(-50%);
  font-size: 0.75rem;
  color: var(--nxt-on-surface-muted);
  pointer-events: none;
}

.ecp__dd {
  position: relative;
}
.ecp__dd-trigger {
  width: 100%;
  height: 2.625rem;
  padding: 0 2.5rem 0 0.875rem;
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.625rem;
  background: var(--nxt-surface-low);
  color: var(--nxt-on-surface);
  font-size: 0.875rem;
  font-family: inherit;
  outline: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  text-align: left;
  transition: border-color 0.15s;
  box-sizing: border-box;
}
.ecp__dd-trigger--open,
.ecp__dd-trigger:focus {
  border-color: var(--nxt-primary);
}
.ecp__dd-placeholder {
  color: var(--nxt-on-surface-muted);
}
.ecp__dd-arrow {
  font-size: 0.75rem;
  color: var(--nxt-on-surface-muted);
  transition: transform 0.2s;
  flex-shrink: 0;
}
.ecp__dd-arrow--open {
  transform: rotate(180deg);
}
.ecp__dd-panel {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  z-index: 200;
  background: var(--nxt-card-bg);
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.75rem;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  overflow: hidden;
}
.ecp__dd-search-wrap {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 0.875rem;
  border-bottom: 1px solid var(--nxt-outline-variant);
}
.ecp__dd-search-icon {
  font-size: 0.75rem;
  color: var(--nxt-on-surface-muted);
  flex-shrink: 0;
}
.ecp__dd-search {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: 0.875rem;
  font-family: inherit;
  color: var(--nxt-on-surface);
}
.ecp__dd-search::placeholder {
  color: var(--nxt-on-surface-muted);
}
.ecp__dd-list {
  list-style: none;
  margin: 0;
  padding: 0.375rem;
  max-height: 200px;
  overflow-y: auto;
}
.ecp__dd-item {
  padding: 0.5rem 0.75rem;
  border-radius: 0.5rem;
  cursor: pointer;
  font-size: 0.875rem;
  color: var(--nxt-on-surface);
  transition: background 0.1s;
}
.ecp__dd-item:hover {
  background: var(--nxt-surface-mid);
}
.ecp__dd-item--active {
  background: var(--nxt-surface-mid);
  font-weight: 700;
  color: var(--nxt-primary);
}
.ecp__dd-empty {
  padding: 0.75rem;
  text-align: center;
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-muted);
}

/* Logo */
.ecp__logo-area {
  display: flex;
  align-items: flex-start;
  gap: 1.25rem;
  flex-wrap: wrap;
}
.ecp__logo-preview {
  width: 5rem;
  height: 5rem;
  border-radius: 0.875rem;
  border: 1px solid var(--nxt-outline-variant);
  background: var(--nxt-surface-low);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;
}
.ecp__logo-preview--empty {
  background: var(--nxt-surface-mid);
}
.ecp__logo-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}
.ecp__logo-ph {
  font-size: 1.75rem;
  color: var(--nxt-on-surface-muted);
}
.ecp__logo-drop {
  flex: 1;
  min-width: 200px;
  border: 1.5px dashed var(--nxt-outline-variant);
  border-radius: 0.875rem;
  padding: 1.25rem 1.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.375rem;
  cursor: pointer;
  transition:
    border-color 0.15s,
    background 0.15s;
  position: relative;
  text-align: center;
}
.ecp__logo-drop:hover,
.ecp__logo-drop--active {
  border-color: var(--nxt-primary);
  background: var(--nxt-surface-low);
}
.ecp__logo-drop-icon {
  font-size: 1.25rem;
  color: var(--nxt-on-surface-muted);
}
.ecp__logo-drop-label {
  font-size: 0.875rem;
  color: var(--nxt-on-surface);
  font-weight: 600;
}
.ecp__logo-drop-link {
  color: var(--nxt-primary);
  text-decoration: underline;
}
.ecp__logo-drop-hint {
  font-size: 0.75rem;
  color: var(--nxt-on-surface-muted);
}
.ecp__logo-input {
  position: absolute;
  inset: 0;
  opacity: 0;
  width: 100%;
  height: 100%;
  cursor: pointer;
}
.ecp__logo-selected {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.875rem;
  border-radius: 0.625rem;
  background: var(--nxt-status-up-bg);
  border: 1px solid var(--nxt-status-up);
  font-size: 0.8125rem;
}
.ecp__logo-selected-icon {
  color: var(--nxt-status-up);
  flex-shrink: 0;
}
.ecp__logo-selected-name {
  flex: 1;
  color: var(--nxt-on-surface);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.ecp__logo-remove {
  background: none;
  border: none;
  cursor: pointer;
  color: var(--nxt-on-surface-muted);
  padding: 0;
  font-size: 0.875rem;
  flex-shrink: 0;
}
.ecp__logo-remove:hover {
  color: var(--nxt-error);
}

/* Items liste */
.ecp__items {
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
}
.ecp__item {
  display: flex;
  align-items: center;
  gap: 0.875rem;
  padding: 0.875rem 1rem;
  background: var(--nxt-surface-low);
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.875rem;
}
.ecp__item-avatar {
  width: 2.25rem;
  height: 2.25rem;
  min-width: 2.25rem;
  border-radius: 50%;
  background: linear-gradient(135deg, #333, #111);
  color: #fff;
  font-size: 0.9375rem;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.ecp__item-avatar--addr {
  background: var(--nxt-surface-mid);
  color: var(--nxt-on-surface-variant);
  font-size: 1rem;
}
.ecp__item-info {
  flex: 1;
  min-width: 0;
}
.ecp__item-name {
  font-size: 0.875rem;
  font-weight: 700;
  color: var(--nxt-on-surface);
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}
.ecp__item-primary {
  font-size: 0.5625rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  padding: 0.175rem 0.5rem;
  border-radius: 0.3rem;
  background: rgba(0, 0, 0, 0.08);
  color: var(--nxt-primary);
  border: 1px solid rgba(0, 0, 0, 0.2);
}
.ecp__item-meta {
  font-size: 0.75rem;
  color: var(--nxt-on-surface-muted);
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  margin-top: 0.15rem;
}
.ecp__item-sep {
  opacity: 0.4;
}
.ecp__item-actions {
  display: flex;
  gap: 0.375rem;
  flex-shrink: 0;
}
.ecp__icon-btn {
  width: 2rem;
  height: 2rem;
  border-radius: 0.5rem;
  border: 1px solid var(--nxt-outline-variant);
  background: var(--nxt-card-bg);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  color: var(--nxt-on-surface-variant);
  cursor: pointer;
  transition: all 0.15s;
}
.ecp__icon-btn:hover:not(:disabled) {
  background: var(--nxt-surface-mid);
  color: var(--nxt-on-surface);
}
.ecp__icon-btn--danger:hover:not(:disabled) {
  background: var(--nxt-error-container);
  color: var(--nxt-error);
  border-color: var(--nxt-error);
}
.ecp__icon-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* Formulaire inline */
.ecp__inline-form {
  background: var(--nxt-surface-low);
  border: 1.5px solid var(--nxt-primary);
  border-radius: 0.875rem;
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
.ecp__inline-form-title {
  margin: 0;
  font-size: 0.875rem;
  font-weight: 700;
  color: var(--nxt-primary);
}
.ecp__inline-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  padding-top: 0.5rem;
  border-top: 1px solid var(--nxt-outline-variant);
}

/* Toggle */
.ecp__toggle {
  display: inline-flex;
  align-items: center;
  cursor: pointer;
}
.ecp__toggle-input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}
.ecp__toggle-track {
  width: 2.5rem;
  height: 1.375rem;
  border-radius: 9999px;
  background: var(--nxt-outline-variant);
  position: relative;
  transition: background 0.2s;
}
.ecp__toggle-input:checked + .ecp__toggle-track {
  background: var(--nxt-primary);
}
.ecp__toggle-thumb {
  position: absolute;
  top: 0.1875rem;
  left: 0.1875rem;
  width: 1rem;
  height: 1rem;
  border-radius: 50%;
  background: #fff;
  transition: transform 0.2s;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}
.ecp__toggle-input:checked + .ecp__toggle-track .ecp__toggle-thumb {
  transform: translateX(1.125rem);
}
.ecp__toggle-label {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--nxt-on-surface);
  cursor: pointer;
}

/* Vide */
.ecp__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 2rem;
  color: var(--nxt-on-surface-muted);
  font-size: 0.875rem;
  text-align: center;
}
.ecp__empty-icon {
  font-size: 1.5rem;
  opacity: 0.25;
}
.ecp__empty p {
  margin: 0;
}

/* Buttons */
.ecp__btn {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  height: 2.5rem;
  padding: 0 1.25rem;
  border-radius: 0.625rem;
  font-size: 0.875rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  white-space: nowrap;
  border: none;
  transition:
    filter 0.15s,
    opacity 0.15s;
}
.ecp__btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
.ecp__btn--primary {
  background: #ffd60a;
  color: #0b1320;
  box-shadow: 0 2px 8px rgba(255, 214, 10, 0.35);
}
.ecp__btn--primary:not(:disabled):hover {
  background: #e6c109;
  box-shadow: 0 6px 16px rgba(255, 214, 10, 0.4);
  transform: translateY(-1px);
}
.ecp__btn--ghost {
  background: var(--nxt-surface-low);
  color: var(--nxt-on-surface-variant);
  border: 1px solid var(--nxt-outline-variant);
}
.ecp__btn--ghost:hover {
  background: var(--nxt-surface-mid);
  color: var(--nxt-on-surface);
}

.ecp__actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding-top: 1.25rem;
  border-top: 1px solid var(--nxt-outline-variant);
}
.ecp__error-banner {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.875rem 1.125rem;
  border-radius: 0.75rem;
  background: var(--nxt-error-container);
  border: 1px solid var(--nxt-on-error-container);
  font-size: 0.875rem;
  color: var(--nxt-on-error-container);
}
.ecp__field-error {
  font-size: 0.8125rem;
  color: var(--nxt-error);
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.375rem;
}

.ecp__spinner {
  width: 1rem;
  height: 1rem;
  border-radius: 50%;
  display: inline-block;
  border: 2px solid var(--nxt-outline-variant);
  border-top-color: var(--nxt-primary);
  animation: ecp-spin 0.7s linear infinite;
}
.ecp__spinner--white {
  border-color: rgba(255, 255, 255, 0.25);
  border-top-color: var(--nxt-on-primary);
}
@keyframes ecp-spin {
  to {
    transform: rotate(360deg);
  }
}

/* Bouton danger */
.ecp__btn--danger {
  background: var(--nxt-error, #e53935);
  color: #fff;
}
.ecp__btn--danger:not(:disabled):hover {
  filter: brightness(1.1);
}

/* Modale overlay */
.ecp__modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(3px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}
.ecp__modal {
  background: var(--nxt-card-bg);
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 1.25rem;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.2);
  padding: 2rem 2rem 1.75rem;
  width: 100%;
  max-width: 420px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  text-align: center;
}
.ecp__modal-icon {
  width: 3.5rem;
  height: 3.5rem;
  border-radius: 50%;
  background: rgba(229, 57, 53, 0.08);
  border: 2px solid rgba(229, 57, 53, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: var(--nxt-error, #e53935);
}
.ecp__modal-title {
  margin: 0;
  font-size: 1.0625rem;
  font-weight: 800;
  color: var(--nxt-on-surface);
}
.ecp__modal-desc {
  margin: 0;
  font-size: 0.875rem;
  color: var(--nxt-on-surface-muted);
  line-height: 1.5;
}
.ecp__modal-desc strong {
  color: var(--nxt-on-surface);
  font-weight: 700;
}
.ecp__modal-actions {
  display: flex;
  gap: 0.625rem;
  justify-content: center;
  width: 100%;
  margin-top: 0.25rem;
}

/* Transition modale */
.ecp-modal-enter-active,
.ecp-modal-leave-active {
  transition:
    opacity 0.18s ease,
    transform 0.18s ease;
}
.ecp-modal-enter-from,
.ecp-modal-leave-to {
  opacity: 0;
}
.ecp-modal-enter-from .ecp__modal,
.ecp-modal-leave-to .ecp__modal {
  transform: scale(0.94) translateY(8px);
}

.ecp__success {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 4rem 2rem;
  text-align: center;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 1.5rem;
  box-shadow: 0 4px 15px rgba(142, 142, 142, 0.06);
}
.ecp__success-icon {
  width: 4rem;
  height: 4rem;
  border-radius: 50%;
  background: var(--nxt-status-up-bg);
  border: 2px solid var(--nxt-status-up);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: var(--nxt-status-up);
}
.ecp__success-title {
  margin: 0;
  font-size: 1.375rem;
  font-weight: 800;
  color: var(--nxt-on-surface);
}
.ecp__success-sub {
  margin: 0;
  font-size: 0.9375rem;
  color: var(--nxt-on-surface-muted);
}
.ecp__success-actions {
  display: flex;
  gap: 0.75rem;
  margin-top: 0.5rem;
  flex-wrap: wrap;
  justify-content: center;
}
</style>

<style>
[data-theme='dark'] .ecp {
  background: #0c1421;
}
[data-theme='dark'] .ecp__header,
[data-theme='dark'] .ecp__body,
[data-theme='dark'] .ecp__success {
  background: rgba(20, 28, 41, 0.8);
  border-color: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .ecp__section-icon {
  background: rgba(255, 255, 255, 0.06);
  border-color: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .ecp__divider {
  background: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .ecp__input,
[data-theme='dark'] .ecp__select,
[data-theme='dark'] .ecp__dd-trigger {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.1);
  color: #fff;
}
[data-theme='dark'] .ecp__input:focus,
[data-theme='dark'] .ecp__select:focus,
[data-theme='dark'] .ecp__dd-trigger--open,
[data-theme='dark'] .ecp__dd-trigger:focus {
  border-color: #ffd60a;
  background: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .ecp__dd-panel {
  background: #111c2e;
  border-color: rgba(255, 255, 255, 0.1);
}
[data-theme='dark'] .ecp__dd-item:hover,
[data-theme='dark'] .ecp__dd-item--active {
  background: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .ecp__dd-item--active {
  color: #ffd60a;
}
[data-theme='dark'] .ecp__item {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .ecp__inline-form {
  background: rgba(255, 255, 255, 0.05);
  border-color: #ffd60a;
}
[data-theme='dark'] .ecp__inline-form-title {
  color: #ffd60a;
}
[data-theme='dark'] .ecp__logo-drop {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.15);
}
[data-theme='dark'] .ecp__logo-selected {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .ecp__btn--ghost {
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.6);
  border-color: rgba(255, 255, 255, 0.1);
}
[data-theme='dark'] .ecp__btn--ghost:hover {
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
}
[data-theme='dark'] .ecp__back {
  background: rgba(255, 255, 255, 0.06);
  border-color: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.6);
}
[data-theme='dark'] .ecp__back:hover {
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
}
[data-theme='dark'] .ecp__modal {
  background: #111c2e;
  border-color: rgba(255, 255, 255, 0.1);
}
</style>
